import type { CSSObject } from "@emotion/react";
import type { Theme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import { sharedNeutrals } from "@themes/tokens/shared";

/**
 * Creates a simple two-color vertical linear gradient.
 *
 * Only interpolates its arguments into a CSS string and never calls a JS color
 * utility, so CSS-variable strings are perfectly safe here and it is left
 * untouched by the MUI v7 migration.
 */
export const createLinearGradient = (
  startColor = "var(--mui-palette-grey-100)",
  endColor = "var(--mui-palette-grey-200)"
): string => {
  return `linear-gradient(180deg, ${startColor} 0%, ${endColor} 100%)`;
};

/**
 * A value is unsafe for JS color utilities (`alpha`/`darken`/`lighten`) when it
 * is a CSS-variable string such as `var(--mui-palette-success-main)`. In MUI v7
 * CSS-variables mode `theme.vars.*` returns exactly these strings, which the
 * color utilities cannot parse.
 */
const isCssVar = (color?: string): color is string =>
  !!color && color.includes("var(");

// ---------------------------------------------------------------------------
// CSS-variable resolution
//
// `theme.vars.*` are CSS-variable strings; their concrete per-scheme values
// live on `theme.colorSchemes.light.*` / `theme.colorSchemes.dark.*`. The
// helpers below recover those concrete values so they can be fed to the JS
// color utilities, following the MUI v7 recommendation (light at root +
// `theme.applyStyles("dark", …)`).
// ---------------------------------------------------------------------------

// Matches a CSS var reference, ignoring any fallback, e.g.
//   var(--mui-palette-success-main)
//   var(--mui-palette-primary-500)
//   var(--mui-ui-table-border, #fff)
const CSS_VAR_RE = /^var\(\s*(--[\w-]+)\s*(?:,.*)?\)$/;
const MUI_VAR_PREFIX = "--mui-";

/** How many token-to-token hops to follow before giving up. */
const MAX_VAR_INDIRECTION = 4;

/**
 * Walks the dash-separated token path (e.g. `palette-primary-main`) into a
 * resolved color-scheme object and returns the concrete string value, or
 * `undefined` if any segment is missing or the leaf is not a usable color.
 *
 * A leaf may itself name another token — the semantic `ui` tokens are defined
 * that way, e.g. `ui.accent.data` is `var(--mui-palette-primary-600)` in dark
 * and `var(--mui-palette-primary-main)` in light. Those hops are followed.
 * Refusing to follow them meant every such token resolved as "unknown" and the
 * caller fell back to `primary.light` — which in the dark schemes is one of
 * the two DARKEST steps, so a progress bar asking for the light accent drew
 * itself nearly black.
 */
const resolveVarValueForScheme = (
  schemeRoot: unknown,
  segments: string[]
): string | undefined => {
  let path = segments;

  for (let hop = 0; hop < MAX_VAR_INDIRECTION; hop++) {
    let current: unknown = schemeRoot;
    for (const segment of path) {
      if (current === null || typeof current !== "object") return undefined;
      // Dynamic token access — the narrowest cast that lets us index by a
      // runtime-derived key without resorting to a broad `any`.
      current = (current as Record<string, unknown>)[segment];
    }
    if (typeof current !== "string") return undefined;
    if (!isCssVar(current)) return current;

    const next = current.match(CSS_VAR_RE)?.[1];
    if (!next || !next.startsWith(MUI_VAR_PREFIX)) return undefined;
    path = next.slice(MUI_VAR_PREFIX.length).split("-");
  }

  return undefined;
};

/**
 * Resolves a MUI palette/ui CSS-variable string to its concrete light and dark
 * values from `theme.colorSchemes`.
 *
 * Returns `null` when the input is not a recognised `--mui-*` variable or the
 * token cannot be found in both schemes — callers must then fall back to a
 * literal color and never pass the variable to a color utility.
 */
export const resolvePaletteVar = (
  theme: Theme,
  cssVar: string
): { dark: string; light: string } | null => {
  const parsed = cssVar.match(CSS_VAR_RE);
  if (!parsed) return null;

  const name = parsed[1];
  if (!name.startsWith(MUI_VAR_PREFIX)) return null;

  const segments = name.slice(MUI_VAR_PREFIX.length).split("-");
  if (segments.length < 2) return null;

  const light = theme.colorSchemes.light
    ? resolveVarValueForScheme(theme.colorSchemes.light, segments)
    : undefined;
  const dark = theme.colorSchemes.dark
    ? resolveVarValueForScheme(theme.colorSchemes.dark, segments)
    : undefined;

  if (!light || !dark) return null;
  return { dark, light };
};

/**
 * `applyStyles` is typed against `@mui/system`'s `CSSObject`, whose index
 * signature is structurally incompatible with `@emotion/react`'s `CSSObject`
 * exposed by these helpers. The runtime shape is identical, so this is a pure
 * type-bridge cast at the interop boundary.
 */
const applyDark = (theme: Theme, styles: CSSObject): CSSObject =>
  theme.applyStyles("dark", styles) as CSSObject;

/**
 * Creates a three-color vertical gradient with shading effect.
 * Lighter at top, main color in middle, darker at bottom.
 *
 * Concrete colors (hex/rgb) are shaded via `lighten`/`darken`. A CSS-variable
 * `mainColor` cannot be parsed, so this string-only helper falls back to a flat
 * `fallbackColor` gradient. Use {@link createSchemeAwareShadedGradient} when the
 * color may be a `theme.vars.*` value and you want it resolved instead.
 */
export const createShadedGradient = (
  mainColor?: string,
  mainColorPosition = 50,
  lightenFactor = 0.08,
  darkenFactor = 0,
  fallbackColor = "var(--mui-palette-primary-light)"
): string => {
  const baseColor = mainColor || fallbackColor;

  if (!baseColor || isCssVar(baseColor)) {
    return `linear-gradient(180deg, ${fallbackColor} 0%, ${fallbackColor} ${mainColorPosition}%, ${fallbackColor} 100%)`;
  }

  const lighter = lighten(baseColor, lightenFactor);
  const darker = darken(baseColor, 0.16 + darkenFactor);

  return `linear-gradient(
    180deg,
    ${lighter} 0%,
    ${baseColor} ${mainColorPosition}%,
    ${darker} 100%
  )`;
};

/**
 * Scheme-aware variant of {@link createShadedGradient}.
 *
 * - Concrete color / no color: behaves exactly like `createShadedGradient`
 *   (including the BRAND_COLOR fallback when no color is given).
 * - CSS-variable color that resolves: emits the light gradient at the root and
 *   the dark gradient inside `theme.applyStyles("dark", …)`, using the matching
 *   palette color in each scheme instead of BRAND_COLOR.
 * - CSS-variable color that cannot be resolved: falls back to BRAND_COLOR.
 *
 * Returns a `background` CSS object meant to be spread into a style object, e.g.
 * `...createSchemeAwareShadedGradient(theme, theme.vars.palette.success.main)`.
 */
export const createSchemeAwareShadedGradient = (
  theme: Theme,
  mainColor?: string,
  mainColorPosition = 50,
  lightenFactor = 0.08,
  darkenFactor = 0,
  fallbackColor = "var(--mui-palette-primary-light)"
): CSSObject => {
  const shade = (color?: string): string =>
    createShadedGradient(
      color,
      mainColorPosition,
      lightenFactor,
      darkenFactor,
      fallbackColor
    );

  // Concrete color (or none): mode-independent, single gradient.
  if (!isCssVar(mainColor)) {
    return { background: shade(mainColor) };
  }

  const resolved = resolvePaletteVar(theme, mainColor);
  if (!resolved) {
    // Unresolvable variable: never feed it to the color utilities.
    return { background: shade(fallbackColor) };
  }

  return {
    background: shade(resolved.light),
    ...applyDark(theme, { background: shade(resolved.dark) })
  };
};

interface ChipStyleOptions {
  highlightColor?: string;
  shadowOpacity?: number;
  textColor?: string;
}

/**
 * Builds chip styles (gradient background, border, inset highlight) from a
 * concrete color. A CSS-variable `baseColor` cannot be parsed, so this helper
 * falls back to a flat `fallbackColor`. Use {@link buildSchemeAwareChipStyles}
 * when the color may be a `theme.vars.*` value and you want it resolved.
 */
export const buildChipStyles = (
  baseColor?: string,
  options: ChipStyleOptions = {},
  fallbackColor = "var(--mui-palette-primary-light)"
) => {
  const {
    highlightColor = sharedNeutrals.white,
    shadowOpacity = 0.25,
    textColor = sharedNeutrals.white
  } = options;

  const safeColor = baseColor || fallbackColor;

  if (!safeColor || isCssVar(safeColor)) {
    return {
      background: createShadedGradient(fallbackColor, 45, 0.12),
      border: `1px solid ${fallbackColor}`,
      boxShadow: `
        0 4px 6px color-mix(in srgb, ${fallbackColor} ${shadowOpacity * 100}%, transparent),
        inset 0 1px 0 ${alpha(highlightColor, 0.35)}
      `,
      color: textColor
    };
  }

  const darker = darken(safeColor, 0.12);
  const border = darken(safeColor, 0.1);

  return {
    background: createShadedGradient(safeColor, 45, 0.12),
    border: `1px solid ${border}`,
    boxShadow: `
      0 4px 6px ${alpha(darker, shadowOpacity)},
      inset 0 1px 0 ${alpha(highlightColor, 0.35)}
    `,
    color: textColor
  };
};

/**
 * Scheme-aware variant of {@link buildChipStyles}.
 *
 * - Concrete color / no color: behaves exactly like `buildChipStyles`
 *   (including the BRAND_COLOR fallback when no color is given).
 * - CSS-variable color that resolves: emits the light chip styles at the root
 *   and the dark chip styles inside `theme.applyStyles("dark", …)`, using the
 *   matching palette color in each scheme instead of BRAND_COLOR.
 * - CSS-variable color that cannot be resolved: falls back to BRAND_COLOR.
 *
 * Returns a CSS object meant to be spread into a style object, e.g.
 * `...buildSchemeAwareChipStyles(theme, theme.vars.palette.surface.base)`.
 */
export const buildSchemeAwareChipStyles = (
  theme: Theme,
  baseColor?: string,
  options: ChipStyleOptions = {},
  fallbackColor = "var(--mui-palette-primary-light)"
): CSSObject => {
  // Concrete color (or none): mode-independent, single style object.
  if (!isCssVar(baseColor)) {
    return buildChipStyles(baseColor, options, fallbackColor);
  }

  const resolved = resolvePaletteVar(theme, baseColor);
  if (!resolved) {
    // Unresolvable variable: never feed it to the color utilities.
    return buildChipStyles(fallbackColor, options, fallbackColor);
  }

  return {
    ...buildChipStyles(resolved.light, options, fallbackColor),
    ...applyDark(theme, buildChipStyles(resolved.dark, options, fallbackColor))
  };
};

export const buildTransparentGlassChipStyles = (theme: Theme): CSSObject => {
  const white = theme.vars.palette.common.white;
  const tint = (percent: number): string =>
    `color-mix(in srgb, ${white} ${percent}%, transparent)`;

  return {
    "&:hover": { background: tint(24) },
    background: tint(12),
    border: `1px solid ${tint(22)}`,
    color: white
  };
};

export const buildSoftChipStyles = (
  baseColor?: string,
  fallbackColor = "var(--mui-palette-primary-light)",
  mode: "light" | "dark" = "light"
): CSSObject => {
  const safeColor =
    !baseColor || isCssVar(baseColor) ? fallbackColor : baseColor;

  if (mode === "dark") {
    return {
      background: alpha(safeColor, 0.15),
      border: `1px solid ${alpha(safeColor, 0.35)}`,
      color: lighten(safeColor, 0.3)
    };
  }

  return {
    background: lighten(safeColor, 0.94),
    border: `1px solid ${lighten(safeColor, 0.8)}`,
    color: safeColor
  };
};

/**
 * Scheme-aware variant of {@link buildSoftChipStyles}.
 *
 * - Concrete color / no color: behaves exactly like `buildSoftChipStyles`
 *   (including the BRAND_COLOR fallback when no color is given).
 * - CSS-variable color that resolves: emits the light chip styles at the root
 *   and the dark chip styles inside `theme.applyStyles("dark", …)`, using the
 *   matching palette color in each scheme instead of BRAND_COLOR.
 * - CSS-variable color that cannot be resolved: falls back to BRAND_COLOR.
 *
 * Returns a CSS object meant to be spread into a style object, e.g.
 * `...buildSchemeAwareSoftChipStyles(theme, mainColor)`.
 */
export const buildSchemeAwareSoftChipStyles = (
  theme: Theme,
  baseColor?: string,
  fallbackColor = "var(--mui-palette-primary-light)"
): CSSObject => {
  // Concrete color (or none): emit the light style at the root and the dark
  // style inside applyStyles so the tint direction follows the active scheme.
  if (!isCssVar(baseColor)) {
    return {
      ...buildSoftChipStyles(baseColor, fallbackColor, "light"),
      ...applyDark(theme, buildSoftChipStyles(baseColor, fallbackColor, "dark"))
    };
  }

  const resolved = resolvePaletteVar(theme, baseColor);
  if (!resolved) {
    // Unresolvable variable: never feed it to the color utilities.
    return {
      ...buildSoftChipStyles(fallbackColor, fallbackColor, "light"),
      ...applyDark(
        theme,
        buildSoftChipStyles(fallbackColor, fallbackColor, "dark")
      )
    };
  }

  return {
    ...buildSoftChipStyles(resolved.light, fallbackColor, "light"),
    ...applyDark(
      theme,
      buildSoftChipStyles(resolved.dark, fallbackColor, "dark")
    )
  };
};
